/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { DataGridPro, GridActionsCellItem } from '@mui/x-data-grid-pro';
import apiAdmin from '../../services/api-admin';
import { MdEdit, MdDelete, MdAdd } from 'react-icons/md';
import { useForm, Controller } from "react-hook-form";
import { Dropzone } from "dropzone";
import "dropzone/dist/dropzone.css";
import { TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTime } from 'luxon';
import OutlinedDiv from '../layout/OutlinedDiv';
import { useOktaAuth } from '@okta/okta-react';
import ReconveyanceOrders from './reconveyance-orders';
import CustomDataGridToolbar from '../layout/custom-toolbar';

const ReconveyanceManagement = (props) => {
    const { register, handleSubmit, setValue, getValues, watch, reset, formState: { errors }, control } = useForm();
    const { authState } = useOktaAuth();
    const [lenders, setLenders] = useState([]);
    const [lenderIdToEdit, setLenderIdToEdit] = useState(null);
    const [lenderToEdit, setLenderToEdit] = useState(null);
    const [lenderIdToDelete, setLenderIdToDelete] = useState(null);
    const [fileDropzone, setFileDropzone] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [deleted, setDeleted] = useState(false);
    const [error, setError] = useState(null);
    const poaDateWatched = watch("powerOfAttorneyDate");
    const [uploadedFileName, setUploadedFileName] = useState("");
    const [linkedCustomers, setLinkedCustomers] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedRowIds, setExpandedRowIds] = useState([]);
    const [reconveyanceOrders, setReconveyanceOrders] = useState([]);
    
    const columns = [
        { field: 'name', headerName: 'Name', flex: 300, },
        { field: 'powerOfAttorney', headerName: 'POA File', flex: 300,
            renderCell: (params) => {
                const file = params.value;
                return file ? (
                    <Link to="#" onClick={(e) => { e.preventDefault(); openDoc('/POA/' + file); }}>
                        {file}
                    </Link>
                ) : (
                    <span className="text-muted">No file</span>
                );
            },
        },
        { field: 'powerOfAttorneyDate', headerName: 'POA Date', flex: 200,
            renderCell: (params) => {
                const rawDate = params.row.powerOfAttorneyDate;
                const date = DateTime.fromISO(String(rawDate));
                const isExpired = date.isValid && date < DateTime.now().minus({ years: 1 });
                const formatted = date.isValid ? date.toLocaleString(DateTime.DATE_MED) : 'No date added';
                return (
                    <span className={isExpired ? 'text-danger fw-bold' : ''}>
                        {formatted}
                    </span>
                );
            },
        },
        { field: 'preferredCustomerCount', headerName: 'Preferred Customers', flex: 150,
            renderCell: (params) => {
                const count = params.row?.linkedCustomers?.length || 0;
                return <span>{count}</span>;
            },
        },
        { field: 'actions', type: 'actions', headerName: 'Actions',flex: 100, cellClassName: 'actions',
            getActions: ({ id }) => [
                <GridActionsCellItem
                    icon={<MdEdit />}
                    label="Edit"
                    onClick={() => { setLenderIdToEdit(id) }}
                    color="inherit"
                />,
                <GridActionsCellItem
                    icon={<MdDelete />}
                    label="Delete"
                    onClick={() => { setLenderIdToDelete(id); setDeleted(false) }}
                    color="inherit"
                    data-bs-toggle="modal"
                    data-bs-target="#deleteModal"
                    role="button"
                />,
            ],
        },
    ];

    useEffect(() => {
        const getLenders = async () => {
            const response = await apiAdmin.get(`/lenders`);
            const lendersData = await Promise.all(response.data.map(async (lender) => {
                const linkedResponse = await apiAdmin.get(`/get-lender-preferred-customers?lenderId=${lender.lenderId}`);
                return { id: lender.lenderId, name: lender.name, powerOfAttorney: lender.powerOfAttorney, powerOfAttorneyDate: lender.powerOfAttorneyDate, linkedCustomers: linkedResponse.data || [], };
            }));
            setLenders(lendersData);
        };

        getLenders();

    }, [submitted, deleted]);

    const startNewLender = () => {
        setLenderIdToEdit(-1); 
        setLenderToEdit(null); 
        setLinkedCustomers([]); 
        setSearchResults([]); 
        setSearchQuery(''); 
        reset(); 
    };

    useEffect(() => {
        if (!lenderIdToEdit || lenderIdToEdit === -1) return;
        const getLender = async () => {
            apiAdmin.get(`/lender?lenderId=${lenderIdToEdit}`)
                .then((response) => {
                    setFileDropzone(null);
                    setLenderToEdit(response.data);
                    setUploadedFileName(response.data.powerOfAttorney);
                    setValue('lenderId', response.data.lenderId); 
                    setValue('name', response.data.name);
                    setValue('billingEmail', response.data.billingEmail);
                    setValue('invoiceRequired', response.data.invoiceRequired);
                    const poaDate = response.data.powerOfAttorneyDate ? DateTime.fromISO(response.data.powerOfAttorneyDate) : null;
                    setValue('powerOfAttorneyDate', poaDate);
                });
        }
        getLender();
    }, [lenderIdToEdit, setValue]);

    useEffect(() => {
        if (!poaDateWatched || lenderIdToEdit == null) return;

        if (fileDropzone) {
            fileDropzone.destroy();
            setFileDropzone(null);
        }

        const dz = new Dropzone("div#file", {
            url: process.env.REACT_APP_API_ADMIN_BASE_URL + "/POA/file", maxFilesize: 15, maxFiles: 1, headers: { 'Authorization': `Bearer ${authState?.accessToken?.accessToken}` },
            init: function () {
                this.on("sending", function (file, xhr, formData) {
                    const poaDate = getValues("powerOfAttorneyDate");
                    const formattedDate = poaDate.toFormat("yyyyLLdd");
                    const lenderId = lenderIdToEdit !== null && lenderIdToEdit !== -1 ? lenderIdToEdit : "temp";
                    const newFileName = `${formattedDate}-${lenderId}.pdf`;
                    formData.append("newFileName", newFileName);
                    setUploadedFileName(newFileName);
                });
            }
        });

        setFileDropzone(dz);
    }, [poaDateWatched, lenderToEdit, lenderIdToEdit]);

    const openDoc = async (blobName) => {
        const res = await apiAdmin.get(`/get-doc-url?blobName=${encodeURIComponent(blobName)}`);
        const sasUrl = res.data;
        window.open(sasUrl, '_blank');
    };
    function EditToolbar(props) {
        return (
            <CustomDataGridToolbar>
                <span className="btn-link btn" onClick={startNewLender}>
                    <MdAdd className="mb-1" /> Add Lender
                </span>
            </CustomDataGridToolbar>
        );
    }

    useEffect(() => {
        if (!lenderIdToEdit || lenderIdToEdit === -1) return;

        const getLinkedCustomers = async () => {
            const response = await apiAdmin.get(`/get-lender-preferred-customers?lenderId=${lenderIdToEdit}`);
            setLinkedCustomers(response.data);
        };

        getLinkedCustomers();
    }, [lenderIdToEdit]);

    useEffect(() => {
        if (!searchQuery) {
            setSearchResults([]);
            return;
        }
        const searchPreferredCustomers = async () => {
            const response = await apiAdmin.get(`/preferred-customers/search?query=${encodeURIComponent(searchQuery)}`);
            setSearchResults(response.data);
        };
        searchPreferredCustomers();
    }, [searchQuery]);


    const linkCustomer = async (preferredCustomerId) => {
      
            await apiAdmin.post(`/add-lender-preferred-customer`, {
                lenderId: lenderIdToEdit,
                preferredCustomerId
            });

            const addedCustomer = searchResults.find(c => c.preferredCustomerId === preferredCustomerId);
            if (addedCustomer) {
                setLinkedCustomers(prev => Array.isArray(prev) ? [...prev, addedCustomer] : [addedCustomer]);
            }
            setSearchResults(prev => prev.filter(c => c.preferredCustomerId !== preferredCustomerId));
            setSearchQuery('');  
    };

    const unlinkCustomer = async (preferredCustomerId) => {
      
        await apiAdmin.post(`/delete-lender-preferred-customer?lenderId=${lenderIdToEdit}&preferredCustomerId=${preferredCustomerId}`);
        setLinkedCustomers((prev) =>
            prev.filter((c) => c.preferredCustomerId !== preferredCustomerId)
        );

    };

    const onSubmit = data => {
        
        const lenderObject = {
            ...data,
            powerOfAttorney: uploadedFileName,
            lenderId: lenderIdToEdit
        }

        const saveLender = async () => {
            await apiAdmin.post(`/save-lender`, lenderObject)
                .then((response) => {
                    if (response.data) {
                        setLenderIdToEdit(null);
                        setLenderToEdit(null);
                        setSubmitted(true);
                        setSearchQuery('');
                        setLinkedCustomers([]);
                        setSearchResults([]);
                        setUploadedFileName('');
                        reset();
                    } else {
                        setError("There was an error saving the lender. Please try again.")
                    }
                });
        }
        saveLender();
    }

    const deleteLenders = () => {
        const deleteLender = async () => {
            await apiAdmin.post(`/delete-lender?lenderId=` + lenderIdToDelete)
                .then((response) => {
                    if (response.statusText === "OK") {
                        setLenderIdToDelete(null);
                        setDeleted(true);
                    } else {
                        setError("There was an error deleting the lender. Please try again.")
                    }
                });
        }
        deleteLender();
    }
    
    const handleDetailPanelExpandedRowIdsChange = useCallback((newIds) => {
        setExpandedRowIds(newIds.length > 1 ? [newIds[newIds.length - 1]] : newIds,);
    }, []);

    return (
        <div>
            <title>Reconveyance Management</title>
            <h1>Reconveyance Management</h1>
            {lenderToEdit || lenderIdToEdit === -1 ? <>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="border p-3">
                        <div className="mb-3">
                            <TextField
                                label="Name *"
                                {...register("name", { required: true })}
                                className="w-100"
                            />
                            {errors?.name?.type === "required" && <small className="text-danger">This field is required</small>}
                        </div>
                        <div className="mb-3">
                            <TextField
                                label="Billing Email *"
                                {...register("billingEmail", { required: true })}
                                className="w-100"
                            />
                            {errors?.billingEmail?.type === "required" && <small className="text-danger">This field is required</small>}
                        </div>
                        <div className="mb-3">
                            <div className="form-check form-switch">
                                <input className="form-check-input" type="checkbox" id="invoiceRequired" {...register("invoiceRequired")} />
                                <label className="form-check-label" htmlFor="invoiceRequired">Invoice Required</label>
                            </div>
                        </div>
                        <div className="mb-3">
                            <Controller
                                name="powerOfAttorneyDate"
                                control={control}
                                rules={{ validate: { dateValid: (value) => !value || DateTime.fromISO(value).isValid || "Invalid date format" } }}
                                render={({ field: { onChange, value } }) => (
                                    <DatePicker value={value} onChange={onChange} className="w-100" label="POA Active Date" />
                                )}
                            />
                        </div>
                        {lenderToEdit?.powerOfAttorney && (
                            <div className="mb-3">
                                <OutlinedDiv label="Current File">
                                    <Link to="#" onClick={(e) => { e.preventDefault(); openDoc('/POA/' + lenderToEdit.powerOfAttorney);}}>{lenderToEdit.powerOfAttorney}</Link>
                                </OutlinedDiv>
                            </div>
                        )}
                        {poaDateWatched && (
                            <div className="mb-3">
                                <OutlinedDiv label="Power of Attorney">
                                    <div id="file" className="dropzone dropzone-previews border-0">
                                        <div className="dz-message" data-dz-message><span>File Upload</span></div>
                                    </div>
                                </OutlinedDiv>
                            </div>
                        )}
                    </div>
                    {lenderIdToEdit != null && lenderIdToEdit !== -1 && (
                        <>
                            <h5 className="mt-3">Linked Preferred Customers</h5>
                            <ul className="list-group mb-3">
                                {linkedCustomers.map(c => (
                                    <li key={c.preferredCustomerId} className="list-group-item d-flex justify-content-between">
                                        {c.fullName} ({c.email})
                                        <button type="button" className="btn btn-link p-0" onClick={() => unlinkCustomer(c.preferredCustomerId)}>
                                            <MdDelete title="Delete" />
                                        </button>
                                    </li>
                                ))}
                            </ul>

                            <TextField
                                label="Search Preferred Customers"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="mb-3 w-100"
                            />

                            <ul className="list-group">
                                {searchResults.map(c => (
                                    <li key={c.preferredCustomerId} className="list-group-item d-flex justify-content-between">
                                        {c.fullName} ({c.email})
                                        <button type="button" className="btn btn-sm" onClick={() => linkCustomer(c.preferredCustomerId)}>
                                            <MdAdd className="mb-1" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}

                    <div className="pt-2">
                        <button type="submit" className="btn btn-primary me-4">Submit</button>
                        <button type="button" className="btn btn-primary" onClick={() => { setLenderIdToEdit(null); setLenderToEdit(null); setSubmitted(false); setError(null); setSearchQuery(''); setLinkedCustomers([]); setSearchResults([]); setUploadedFileName(''); reset(); }}>Cancel</button>
                    </div>
                </form>
            </>
                :
                <>
                    {submitted && <div className="alert alert-success alert-dismissible" role="alert">Lender saved successfully! You can now add preferred customers.<button type="button" className="btn-close" onClick={() => setSubmitted(false)} aria-label="Close"></button></div>}
                    {deleted && <div className="alert alert-warning alert-dismissible" role="alert">Lender deleted successfully!<button type="button" className="btn-close" onClick={() => setDeleted(false)} aria-label="Close"></button></div>}
                    {error && <div className="alert alert-danger" role="alert">{error}</div>}
                    <h2>Lender Management</h2>
                    <DataGridPro
                        rows={lenders}
                        columns={columns}
                        loading={lenders.length === 0}
                        slotProps={{
                            loadingOverlay: {
                                variant: 'skeleton',
                                noRowsVariant: 'skeleton',
                            },
                        }}
                        autoHeight
                        rowHeight={38}
                        disableRowSelectionOnClick
                        alignItems="center"
                        slots={{
                            toolbar: EditToolbar,
                        }}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25, page: 0 },
                            },
                        }}
                        pagination
                        getDetailPanelHeight={({ row }) => 'auto'}
                        onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
                        detailPanelExpandedRowIds={expandedRowIds}
                        showToolbar
                    />
                    <h2 className="pt-2">Order Management</h2>
                    <ReconveyanceOrders reconveyanceOrders={reconveyanceOrders} setReconveyanceOrders={setReconveyanceOrders} openDoc={openDoc} />
                </>
            }
            <div className="modal shown" tabIndex="-1" id="deleteModal" >
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Delete Lender?</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete this lender?</p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">No, Cancel</button>
                            <button type="button" className="btn btn-primary" data-bs-dismiss="modal" onClick={() => deleteLenders()}>Yes, Delete</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReconveyanceManagement;
